import React, { useEffect, useState } from 'react'
import { getAuditLog } from '@/api/audit'
import { AuditLog } from '@/types/index'
import { formatDateTime } from '@/utils/dateFormat'

interface AuditHistoryTabProps {
  /** The record's id. Passed to the API as `entity_id`. */
  entityId: string
  /** Noun used in the empty state, e.g. "company". */
  entityLabel: string
}

// Bug #331 (and the leftover #328 carried): both the company and the project
// detail pages rendered a hard-coded "Audit history for this X will appear
// here." The blocker was real — `GET /api/v1/audit` filtered on entity_type
// only, so there was no way to ask for one record's history. That filter is
// added alongside this component.
//
// Filtering is on `entity_id` alone, deliberately. It is a uuid, so it is
// selective by itself and a second predicate buys nothing — which is also
// why this tab was unaffected by Bug #333, where `entity_type` carried two
// conventions at once (the oldest routers wrote "company"/"project" while
// every other module wrote table names) and an entity_type predicate would
// have shown only the half of a record's history that came from one code
// path. #333 standardised every writer on the table name and backfilled the
// existing rows, so that hazard is gone; the entity_id filter stays because
// it is still the right filter.
//
// One component for both pages: the two were about to be identical, and the
// 403 handling below is the sort of thing that gets fixed in one copy only.
export const AuditHistoryTab: React.FC<AuditHistoryTabProps> = ({
  entityId,
  entityLabel,
}) => {
  const [entries, setEntries] = useState<AuditLog[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [forbidden, setForbidden] = useState(false)

  useEffect(() => {
    let cancelled = false

    const load = async () => {
      if (!entityId) return
      setLoading(true)
      setError(null)
      setForbidden(false)
      try {
        const data = await getAuditLog({
          entityId,
          pageSize: 50,
          sortBy: 'created_at',
          sortOrder: 'desc',
        })
        if (!cancelled) setEntries(data.items)
      } catch (err) {
        if (cancelled) return
        // `GET /audit` requires platform_admin, and `api/client.ts` turns a
        // 403 into a PermissionError. A non-admin opening this tab is a
        // normal thing to happen, not an error worth a red box.
        if (err instanceof Error && err.name === 'PermissionError') {
          setForbidden(true)
        } else {
          setError(err instanceof Error ? err.message : 'Failed to load audit history')
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    void load()
    return () => {
      cancelled = true
    }
  }, [entityId])

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '40px 20px', color: 'var(--color-dark-grey)' }}>
        Loading audit history...
      </div>
    )
  }

  if (forbidden) {
    return (
      <div style={{ color: 'var(--color-dark-grey)', fontSize: '14px' }}>
        Audit history is only visible to platform administrators.
      </div>
    )
  }

  if (error) {
    return (
      <div style={{ color: '#dc2626', fontSize: '14px' }}>{error}</div>
    )
  }

  if (entries.length === 0) {
    return (
      <div style={{ textAlign: 'center', padding: '40px 20px', color: 'var(--color-dark-grey)' }}>
        No recorded changes for this {entityLabel} yet.
      </div>
    )
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
      {entries.map((entry) => (
        <div
          key={entry.id}
          style={{
            padding: '12px 14px',
            backgroundColor: 'var(--color-white)',
            border: '1px solid var(--color-light-grey)',
            borderRadius: '6px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: '16px',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', minWidth: 0 }}>
            <span
              style={{
                display: 'inline-block',
                padding: '4px 10px',
                borderRadius: '4px',
                fontSize: '12px',
                fontWeight: 600,
                backgroundColor: '#dbeafe',
                color: '#0284c7',
                flexShrink: 0,
              }}
            >
              {entry.action}
            </span>
            <span
              style={{
                fontSize: '13px',
                color: 'var(--color-dark-grey)',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {/* No user id means an integration did it, not a person —
                  the HubSpot pipeline writes its rows with user_id NULL. */}
              {entry.userId ? `by ${entry.userId}` : 'by an integration'}
            </span>
          </div>
          <span style={{ fontSize: '13px', color: 'var(--color-dark-grey)', flexShrink: 0 }}>
            {formatDateTime(entry.createdAt)}
          </span>
        </div>
      ))}
    </div>
  )
}

export default AuditHistoryTab
